import { Buffer } from 'buffer';

export const openInNewTab = (url: string): void => {
  const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
  if (newWindow) newWindow.opener = null;
};

export const onClickUrl =
  (url: string): (() => void) =>
  () =>
    openInNewTab(url);

export function ellipseText(text = '', maxLength = 9999): string {
  if (text.length <= maxLength) {
    return text;
  }
  const _maxLength = maxLength - 3;
  let ellipse = false;
  let currentLength = 0;
  const result =
    text
      .split(' ')
      .filter((word) => {
        currentLength += word.length;
        if (ellipse || currentLength >= _maxLength) {
          ellipse = true;
          return false;
        } else {
          return true;
        }
      })
      .join(' ') + '...';
  return result;
}

export function ellipseAddress(address = '', width = 6): string {
  return `${address.slice(0, width)}...${address.slice(-width)}`;
}

export function padLeft(n: string, width: number, z?: string): string {
  z = z || '0';
  n = n + '';
  return n.length >= width ? n : new Array(width - n.length + 1).join(z) + n;
}

export function sanitizeHex(hex: string): string {
  hex = hex.substring(0, 2) === '0x' ? hex.substring(2) : hex;
  if (hex === '') {
    return '';
  }
  hex = hex.length % 2 !== 0 ? '0' + hex : hex;
  return '0x' + hex;
}

export function removeHexPrefix(hex: string): string {
  return hex.toLowerCase().replace('0x', '');
}

export function getDataString(func: string, arrVals: any[]): string {
  let val = '';
  for (let i = 0; i < arrVals.length; i++) {
    val += padLeft(arrVals[i], 64);
  }
  const data = func + val;
  return data;
}

export function isMobile(): boolean {
  let mobile = false;

  function hasTouchEvent(): boolean {
    try {
      document.createEvent('TouchEvent');
      return true;
    } catch (e) {
      return false;
    }
  }

  function hasMobileUserAgent(): boolean {
    if (
      /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|ipad|iris|kindle|Android|Silk|lge |maemo|midp|mmp|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows (ce|phone)|xda|xiino/i.test(
        navigator.userAgent
      ) ||
      /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw-(n|u)|c55\/|capi|ccwa|cdm-|cell|chtm|cldc|cmd-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc-s|devi|dica|dmob|do(c|p)o|ds(12|-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(-|_)|g1 u|g560|gene|gf-5|g-mo|go(.w|od)|gr(ad|un)|haie|hcit|hd-(m|p|t)|hei-|hi(pt|ta)|hp( i|ip)|hs-c|ht(c(-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i-(20|go|ma)|i230|iac( |-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|-[a-w])|libw|lynx|m1-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|-([1-8]|c))|phil|pire|pl(ay|uc)|pn-2|po(ck|rt|se)|prox|psio|pt-g|qa-a|qc(07|12|21|32|60|-[2-7]|i-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h-|oo|p-)|sdk\/|se(c(-|0|1)|47|mc|nd|ri)|sgh-|shar|sie(-|m)|sk-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h-|v-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl-|tdg-|tel(i|m)|tim-|t-mo|to(pl|sh)|ts(70|-|m3|m5)|tx-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas-|your|zeto|zte-/i.test(
        navigator.userAgent.substr(0, 4)
      )
    ) {
      return true;
    } else if (hasTouchEvent()) {
      return true;
    }
    return false;
  }
  mobile = hasMobileUserAgent();
  return mobile;
}

export const displayDecimal = (amount: number, decimals: number) => {
  return +(amount / 10 ** decimals).toFixed(6);
};

export const checkValidImage = async (url: string, signal: any) => {
  try {
    const validUrl = (str: string) => {
      var pattern = new RegExp(
        '^(https?:\\/\\/)?' + // protocol
          '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
          '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
          '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
          '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
          '(\\#[-a-z\\d_]*)?$',
        'i'
      ); // fragment locator
      return !!pattern.test(str);
    };
    if (!validUrl(url)) return false;

    let ends = [
      '.cs',
      '.com',
      '.app',
      '.io',
      '.net',
      '.org',
      '.co',
      '.us',
      '.uk',
      '.gov',
      '.info',
      '.xyz',
      '.site',
      '/',
      '\\',
    ];
    let hostEndsWith = (url: string, ends: string[]) => {
      let value = false;
      value = ends.some((element) => {
        return url.endsWith(element);
      });
      return value;
    };
    if (hostEndsWith(url, ends)) return false;
    const res = await fetch(url, signal);

    if (!res.ok) return false;
    const buff = await res.blob();
    const imgCheck = buff.type.startsWith('image/');
    return imgCheck;
  } catch (err) {
    return false;
  }
};

export const checkValidIpfsImage = async (url: string, signal: any) => {
  try {
    let newUrl = '';
    if (url.indexOf('Qm') > -1) {
      const hash = url.substring(url.indexOf('Qm'), url.indexOf('Qm') + 46);
      newUrl = `https://cf-ipfs.com/ipfs/${hash}`;
    } else {
      newUrl = url;
    }
    const res = await fetch(newUrl, signal);
    if (!res.ok) return false;
    const buff = await res.blob();
    // const imgCheck = buff.type.startsWith('application/json');
    const imgCheck = buff.type.startsWith('image/');
    return imgCheck;
  } catch (err) {
    return false;
  }
};

export const stringToColor = (str: string, opacity: number = 1) => {
  let hash = 0;
  for (var i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return `hsla(${hash % 360}, 90%, 50%,${opacity})`;
};

export const truncAddress = (address: string) => {
  const truncName =
    address.length > 8 ? address?.slice(0, 4) + '...' + address?.slice(-4) : address;
  return truncName;
};

export function abbreviateNumber(num: number, locale: string = 'en-US') {
  let replaceString;
  if (locale === 'en-US') {
    replaceString = '.';
  } else if (locale?.includes('de')) {
    replaceString = ',';
  } else if (locale?.includes('fr')) {
    replaceString = ',';
  } else {
    replaceString = '.';
  }

  if (num >= 1000000000000) {
    return (num / 1000000000000).toFixed(3).replace('.', replaceString) + 'T';
  }
  if (num >= 1000000000) {
    return (num / 1000000000).toFixed(3).replace('.', replaceString) + 'B';
  }
  if (num >= 1000000) {
    return (num / 1000000).toFixed(3).replace('.', replaceString) + 'M';
  }
  return num;
}

export function formatNumber(num: number, locale: string = 'en-US') {
  if (num) {
    return new Intl.NumberFormat(locale).format(num);
  } else return '0';
}

export function toBinary(string: string) {
  return Buffer.from(string).toString('base64');
}

export function fromBinary(encoded: Uint8Array) {
  return Buffer.from(encoded).toString();
}

export function fromBase64(encoded: string) {
  return Buffer.from(encoded, 'base64').toString();
}

export function isEmpty(obj: {}) {
  if (typeof obj === 'undefined') return true;
  else return Object.keys(obj)?.length === 0;
}

export function pick<T extends object, U extends keyof T>(obj: T, paths: Array<U>): Pick<T, U> {
  const ret = Object.create(null);
  for (const k of paths) {
    ret[k] = obj[k];
  }
  return ret;
}

export function getDuplicates(array: [], key: string) {
  return array.filter((e1: any) => {
    if (
      array.filter((e2: any) => {
        return e1[key] === e2[key];
      }).length > 1
    ) {
      return e1;
    } else return false;
  });
}

export const getSeparators = (locale: string = 'en-US') => {
  let thousandSeparator = ',',
    decimalSeparator = '.';
  if (locale?.includes('de')) {
    thousandSeparator = '.';
    decimalSeparator = ',';
  } else if (locale?.includes('fr')) {
    thousandSeparator = ' ';
    decimalSeparator = ',';
  } else {
    thousandSeparator = ',';
    decimalSeparator = '.';
  }
  return {
    thousandSeparator: thousandSeparator,
    decimalSeparator: decimalSeparator,
  };
};
